import { useRouter } from 'next/router'
import { Trans, t } from '@lingui/macro'
import imageUrlBuilder from '@sanity/image-url'
import client from '@lib/sanity'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { FaExternalLinkSquareAlt, FaMapMarkerAlt } from 'react-icons/fa'
import {
  FaXTwitter,
  FaFacebook,
  FaLinkedin,
  FaGlobe,
  FaArrowRight,
  FaInstagram,
  FaBluesky,
} from 'react-icons/fa6'
import TranslationProvider, {
  useTranslations,
} from 'providers/TranslationsProvider'
import H2 from 'components/ui/H2'
import Header from 'components/ui/Header'
import Content from 'components/Content'
import { locales, localeLabels } from '@lib/i18n'
import SidebarHeading from 'components/ui/SidebarHeading'
import SidebarSection from 'components/ui/SidebarSection'
import SidebarLink from 'components/ui/SidebarLink'
import Index from 'components/Index'
import ButtonLink from 'components/ButtonLink'
import { useEffect } from 'react'

const MemberView = ({ member, posts }) => {
  const { locale } = useRouter()
  const { init, headLinks } = useTranslations()

  useEffect(() => {
    init()
  }, [])

  const translations = () => {
    const out = []
    for (const loc in locales) {
      if (member.body && member.body[loc] && loc !== locale) {
        out.push(
          <li key={`translation-${loc}`}>
            <Link
              href="/members/[slug]"
              as={`/members/${member.slug.current}`}
              locale={loc}
            >
              <a className="block">
                <Trans id={localeLabels[loc].id} />
              </a>
            </Link>
          </li>
        )
      }
    }
    return <ul className="grid grid-cols-2 md:grid-cols-1">{out}</ul>
  }

  return (
    <div>
      <article className="bg-accent-200 pb-10">
        <Head>
          <title key="title">
            {`${member.name} - ${process.env.NEXT_PUBLIC_SITE_TITLE}`}
          </title>
          <meta property="og:title" content={member.name} key="og:title" />
          <meta property="og:type" content="profile" key="og:type" />
          <link
            rel="canonical"
            href={`${process.env.NEXT_PUBLIC_DOMAIN}${
              locale == 'en' ? `` : `/${locale}`
            }/members/${member.slug.current}`}
            key="canonical"
          />
          <meta
            property="og:url"
            content={`${process.env.NEXT_PUBLIC_DOMAIN}${
              locale == 'en' ? `` : `/${locale}`
            }/members/${member.slug.current}`}
            key="og:url"
          />
          {member.mainImage && member.mainImage.asset && (
            <meta
              property="og:image"
              content={imageUrlBuilder(client)
                .image(member.mainImage)
                .width(600)
                .url()}
              key="og:image"
            />
          )}
          {headLinks()}
        </Head>
        <div className="lg:hidden">
          {member.mainImage && (
            <div className="relative">
              <div className="relative aspect-video w-full bg-accent-100 md:aspect-[3/1]">
                <Image
                  src={imageUrlBuilder(client)
                    .image(member.mainImage)
                    .width(600)
                    .url()}
                  alt={member.name}
                  layout="fill"
                  objectFit="contain"
                  objectPosition="center top"
                  priority={true}
                />
              </div>
            </div>
          )}
        </div>
        <div className="container">
          <div className="lg:mx-auto lg:flex lg:max-w-2/3 lg:flex-row lg:items-center lg:justify-center">
            <div className="hidden lg:mr-10 lg:block">
              {member.mainImage && (
                <div className="relative mt-10 mb-10 hidden aspect-square w-36 border border-primary-100 bg-idpcBackground lg:block">
                  <Image
                    src={imageUrlBuilder(client)
                      .image(member.mainImage)
                      .width(160)
                      .url()}
                    alt={member.name}
                    layout="fill"
                  />
                </div>
              )}
            </div>
            <Header>
              <p className="mb-4 text-center font-medium uppercase lg:text-left 2xl:mb-10">
                <Link href="/members">
                  <a>
                    <Trans>IDPC Member</Trans>
                  </a>
                </Link>
              </p>
              <div className="flex flex-row items-center justify-center lg:justify-start">
                <h1 className="text-center text-2xl font-bold text-primary-500 md:text-4xl lg:text-left 2xl:text-6xl">
                  {member.name}
                </h1>
              </div>
            </Header>
          </div>
          <div className="flex flex-col md:flex-row">
            <div className="mb-10 md:mr-10 md:basis-2/3">
              {member.body && member.body[locale] && (
                <Content content={member.body[locale]} />
              )}
            </div>
            <div className="md:basis-1/3">
              <SidebarSection>
                {member.website && (
                  <SidebarLink
                    icon={<FaExternalLinkSquareAlt />}
                    href={member.website}
                    target="_blank"
                  >
                    <Trans>Visit website</Trans>
                  </SidebarLink>
                )}
                {member.bluesky && (
                  <SidebarLink
                    icon={<FaBluesky />}
                    href={member.bluesky}
                    target="_blank"
                  >
                    <Trans>Bluesky</Trans>
                  </SidebarLink>
                )}
                {member.twitter && (
                  <SidebarLink
                    icon={<FaXTwitter />}
                    href={member.twitter}
                    target="_blank"
                  >
                    <Trans>X</Trans>
                  </SidebarLink>
                )}
                {member.facebook && (
                  <SidebarLink
                    icon={<FaFacebook />}
                    href={member.facebook}
                    target="_blank"
                  >
                    <Trans>Facebook</Trans>
                  </SidebarLink>
                )}
                {member.instagram && (
                  <SidebarLink
                    icon={<FaInstagram />}
                    href={member.instagram}
                    target="_blank"
                  >
                    <Trans>Instagram</Trans>
                  </SidebarLink>
                )}
                {member.linkedin && (
                  <SidebarLink
                    icon={<FaLinkedin />}
                    href={member.linkedin}
                    target="_blank"
                  >
                    <Trans>LinkedIn</Trans>
                  </SidebarLink>
                )}
              </SidebarSection>
              {member.region && member.region.length && (
                <SidebarSection>
                  <SidebarHeading>
                    <FaMapMarkerAlt className="mr-2" />
                    <Trans>Region</Trans>
                  </SidebarHeading>
                  <div className="mb-4">
                    <ul>
                      {member.region.map((region) => (
                        <li key={region._id}>
                          <Link
                            href="/regions/[slug]"
                            as={`/regions/${region.slug[locale].current}`}
                          >
                            <a className="block py-2">{region.title[locale]}</a>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </SidebarSection>
              )}
              <SidebarSection>
                <SidebarHeading>
                  <FaGlobe className="mr-2" />
                  <Trans>Translations</Trans>
                </SidebarHeading>
                <div className="mb-4">{translations()}</div>
              </SidebarSection>
            </div>
          </div>
        </div>
      </article>
      {posts && !!posts.length && (
        <div className="py-10">
          <div className="container">
            <H2>
              <Trans>Related Content</Trans>
            </H2>
            <Index posts={posts} />
            <div className="mt-4 flex justify-center md:mt-8 lg:mt-10">
              <ButtonLink
                href={{
                  pathname: '/search',
                  query: { p: member.slug.current },
                }}
              >
                <span className="mr-2 leading-tight">
                  <Trans>Explore more content</Trans>
                </span>
                <FaArrowRight />
              </ButtonLink>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
export default MemberView
